import { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PowerOffIcon from "@mui/icons-material/PowerOff";

import { ApiError, getAgents, listUsers } from "./api";
import { colors } from "./theme";

const POLL_MS = 60_000;

type Counts = {
  // null = source not loaded yet OR caller doesn't have permission to read it
  pendingUsers: number | null;
  pendingPlus: boolean; // true if exact count is "60+" due to pagination
  offlineHelpers: number | null;
};

type Props = {
  isAdmin: boolean; // gates the pending-users source (admin lambda is admins-only)
  onJumpToTab: (tab: "users" | "agents") => void;
};

// Subtle header bell. Badge total = sum of sources the caller can see. The
// popover is purely informational — every line links to a tab where the
// detail lives. We deliberately don't show user lists / agent lists inline
// (that's what the tabs are for); the bell exists to draw attention.
export default function Notifications({ isAdmin, onJumpToTab }: Props) {
  const [counts, setCounts] = useState<Counts>({
    pendingUsers: null,
    pendingPlus: false,
    offlineHelpers: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // Track mount state so polling doesn't set state on an unmounted component.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Parallel + tolerant: one source failing shouldn't blank the other.
    // Admins fetch both sources; non-admins skip the users call (it'd 403
    // since the admin Lambda gates on the admins group).
    const usersPromise = isAdmin ? listUsers() : Promise.resolve(null);
    const agentsPromise = getAgents();

    const [usersResult, agentsResult] = await Promise.allSettled([usersPromise, agentsPromise]);

    if (!mountedRef.current) return;

    let pendingUsers: number | null = null;
    let pendingPlus = false;
    if (usersResult.status === "fulfilled" && usersResult.value) {
      const u = usersResult.value;
      // Pending = enabled + zero groups. Matches UsersPage's pending bucket.
      pendingUsers = u.users.filter((x) => x.enabled && x.groups.length === 0).length;
      // Cognito ListUsers caps at 60 per page; presence of a token means the
      // first page didn't enumerate the whole pool. Show "60+" rather than
      // walking pages on every poll.
      pendingPlus = !!u.nextPaginationToken;
    }

    let offlineHelpers: number | null = null;
    if (agentsResult.status === "fulfilled") {
      offlineHelpers = agentsResult.value.agents.filter(
        (a) => a.staleness === "offline",
      ).length;
    }

    // If both calls failed, surface a short error. Single-source failures stay
    // silent — the missing line just doesn't render.
    if (
      (isAdmin && usersResult.status === "rejected") &&
      agentsResult.status === "rejected"
    ) {
      const reason = agentsResult.reason;
      setError(reason instanceof ApiError ? `${reason.status}: ${reason.body}` : String(reason));
    }

    setCounts({ pendingUsers, pendingPlus, offlineHelpers });
    setLoading(false);
  }, [isAdmin]);

  // Initial load + background poll.
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const badgeCount =
    (counts.pendingUsers ?? 0) + (counts.offlineHelpers ?? 0);

  function handleOpen(e: React.MouseEvent<HTMLElement>) {
    setAnchorEl(e.currentTarget);
    // Refresh immediately when the user opens the popover so they see
    // current state, not whatever the 60s timer last fetched.
    refresh();
  }

  function handleJump(tab: "users" | "agents") {
    setAnchorEl(null);
    onJumpToTab(tab);
  }

  const showPending = isAdmin && counts.pendingUsers !== null;
  const showOffline = counts.offlineHelpers !== null;
  const anythingToShow = (showPending && counts.pendingUsers! > 0) || (showOffline && counts.offlineHelpers! > 0);

  return (
    <>
      <Tooltip title={badgeCount > 0 ? `${badgeCount} item${badgeCount === 1 ? "" : "s"} need attention` : "All clear"}>
        <IconButton size="small" onClick={handleOpen} aria-label="Notifications">
          <Badge
            badgeContent={badgeCount > 99 ? "99+" : badgeCount}
            color="error"
            invisible={badgeCount === 0}
            overlap="circular"
          >
            <NotificationsIcon fontSize="small" sx={{ color: colors.gray700 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 320, p: 0 } } }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
              Notifications
            </Typography>
            {loading && <CircularProgress size={14} />}
          </Stack>
        </Box>
        <Divider />

        {error && (
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" sx={{ color: colors.warning }}>{error}</Typography>
          </Box>
        )}

        {!anythingToShow && !loading && !error && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: colors.gray500 }}>
              All clear.
            </Typography>
          </Box>
        )}

        <Stack>
          {showPending && counts.pendingUsers! > 0 && (
            <NotificationRow
              icon={<PersonAddIcon fontSize="small" />}
              title={`${counts.pendingUsers}${counts.pendingPlus ? "+" : ""} pending user${counts.pendingUsers === 1 && !counts.pendingPlus ? "" : "s"}`}
              detail="Awaiting group assignment before they can use the system."
              actionLabel="Review"
              onAction={() => handleJump("users")}
            />
          )}
          {showOffline && counts.offlineHelpers! > 0 && (
            <NotificationRow
              icon={<PowerOffIcon fontSize="small" />}
              title={`${counts.offlineHelpers} helper${counts.offlineHelpers === 1 ? "" : "s"} offline`}
              detail="No heartbeat received in the last hour. Recordings may be missing."
              actionLabel="View fleet"
              onAction={() => handleJump("agents")}
            />
          )}
        </Stack>
      </Popover>
    </>
  );
}

function NotificationRow({
  icon,
  title,
  detail,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        "&:not(:last-child)": { borderBottom: `1px solid ${colors.gray200}` },
      }}
    >
      <Box sx={{ color: colors.gray600, pt: 0.25 }}>{icon}</Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{title}</Typography>
        <Typography variant="caption" sx={{ color: colors.gray500, display: "block", mt: 0.25 }}>
          {detail}
        </Typography>
      </Box>
      <Button size="small" onClick={onAction}>{actionLabel}</Button>
    </Box>
  );
}
